import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import { useIntro } from '../utils/hydration';
import Icon from './Icon';
import ServiceGlyph from './ServiceGlyph';
import { serviceGroups, serviceMeta } from '../content/services';
import { PHONE, EMAIL, TELEGRAM, VIBER } from '../content/contacts';

/**
 * Шапка.
 *
 * Меню. Було сім пунктів у ряд — «Головна», «Про компанію», «Послуги»,
 * «Цінова політика», «Блог», «Питання», «Контакти» — і на 1024px вони
 * впритул торкалися логотипа й кнопки. Власник попросив менше пунктів і
 * випадне меню. Тепер пунктів чотири: два з випадним списком і два прості.
 *
 *   Послуги ▾   шість напрямків із малюнками й одним реченням про кожен,
 *               під ними — «Усі послуги», «План запуску», «З чого почати»
 *   Компанія ▾  «Про компанію», «Часті запитання», «Блог»
 *   Ціни        /tsinova-polityka
 *   Контакти    /kontakty
 *
 * «Головна» пішла: на головну веде логотип. Кожен пункт із випадним списком
 * сам є посиланням («Послуги» відкриває /poslugy), а список розгортається
 * наведенням; поруч стоїть окрема кнопка-стрілка для тих, хто працює з
 * клавіатури або з екрана без наведення — вона розгортає й згортає список.
 * Так клік по слову завжди веде туди, куди написано, а не перемикає панель.
 *
 * Тон. Шапка дивиться, що лежить під її нижнім краєм, і бере той самий тон:
 * над відео першого екрана й над банерами внутрішніх сторінок
 * (data-tone="dark") стає темним склом, над світлим — білою. На самому
 * початку головної — прозора. Перевірка йде в rAF на прокрутці: один
 * elementFromPoint на кадр.
 */
const SERVICE_LINKS = serviceMeta.map((m, i) => ({
  to: `/poslugy/${m.slug}`,
  title: serviceGroups[i].title,
  text: serviceGroups[i].subtitle,
  index: i
}));

const SERVICES_MORE = [
  { to: '/poslugy', label: 'Усі послуги' },
  { to: '/plan-zapusku-kliniky', label: 'План запуску клініки' },
  { to: '/z-chogo-pochaty', label: 'З чого почати' }
];

const COMPANY_LINKS = [
  { to: '/pro-kompaniyu', label: 'Про компанію', text: 'Візія, місія та порядок роботи' },
  { to: '/pytannya', label: 'Часті запитання', text: 'Те, що питають у першій розмові' },
  { to: '/blog', label: 'Блог', text: 'Зміни в медичному законодавстві' }
];

const TOP_LINKS = [
  { to: '/tsinova-polityka', label: 'Ціни' },
  { to: '/kontakty', label: 'Контакти' }
];

// Пункти мобільного меню — ті самі адреси, згруповані.
const MOBILE_GROUPS = [
  { title: 'Послуги', links: [...SERVICE_LINKS.map((s) => ({ to: s.to, label: s.title })), ...SERVICES_MORE.slice(1), SERVICES_MORE[0]] },
  { title: 'Компанія', links: [...COMPANY_LINKS.map((c) => ({ to: c.to, label: c.label })), ...TOP_LINKS] }
];

const Chevron = ({ className = '' }) => (
  <svg viewBox="0 0 20 20" className={`w-4 h-4 ${className}`} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="m5 7.5 5 5 5-5" />
  </svg>
);

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Лише те, що справді можна зараз сфокусувати: елементи в прихованих
// (display: none) блоках getClientRects не мають.
const focusablesIn = (node) =>
  node ? Array.from(node.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0) : [];

// aria-current="page" — лише точний збіг адреси. «Послуги» на сторінці
// окремої послуги підсвічено як розділ, але поточна сторінка там інша.
const currentAttr = (pathname, to) => (pathname === to ? 'page' : undefined);

/**
 * Пункт із випадним списком.
 *
 * Оголошений поза Navbar навмисно. Коли він жив усередині, кожен рендер
 * шапки (а вона рендериться на кожну зміну тону під час прокрутки й на кожне
 * відкриття списку) створював новий тип компонента, і React перемонтовував
 * пункт цілком: кнопка, на якій стояв фокус, зникала з DOM, і фокус падав на
 * <body>. Тож із клавіатури список відкривався, але зайти в нього було не можна.
 *
 * Поведінка — шаблон disclosure: стрілка — кнопка з aria-expanded, фокус
 * лишається на ній, Tab веде в панель, Escape згортає й повертає на кнопку,
 * фокус, що пішов за межі пункту, згортає панель. Стрілка вниз із кнопки
 * відкриває панель і ставить фокус на перший пункт, стрілки вгору-вниз
 * ходять пунктами панелі.
 */
const Dropdown = ({
  id, to, label, width, pathname, isOpen, active, onDark, pillClass, wordClass,
  onOpen, onClose, onScheduleClose, children
}) => {
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const hoverRef = useRef(false);
  const focusFirstRef = useRef(false);

  // Стрілка вниз на згорнутій кнопці: панель з'являється лише в наступному
  // коміті, тож фокус переносимо після нього.
  useEffect(() => {
    if (!isOpen || !focusFirstRef.current) return;
    focusFirstRef.current = false;
    const [first] = focusablesIn(panelRef.current);
    if (first) first.focus();
  }, [isOpen]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      onClose();
      if (buttonRef.current) buttonRef.current.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    if (event.target === buttonRef.current) {
      if (event.key !== 'ArrowDown') return;
      event.preventDefault();
      if (isOpen) {
        const [first] = focusablesIn(panelRef.current);
        if (first) first.focus();
      } else {
        focusFirstRef.current = true;
        onOpen();
      }
      return;
    }

    const items = focusablesIn(panelRef.current);
    const index = items.indexOf(event.target);
    if (index === -1) return;
    event.preventDefault();
    if (event.key === 'ArrowDown') {
      if (index < items.length - 1) items[index + 1].focus();
    } else if (index > 0) {
      items[index - 1].focus();
    } else if (buttonRef.current) {
      buttonRef.current.focus();
    }
  };

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={() => {
        hoverRef.current = true;
        onOpen();
      }}
      onMouseLeave={() => {
        hoverRef.current = false;
        // Фокус усередині — людина працює з клавіатури або клікнула стрілку.
        // Згорнути панель з-під фокусу означало б викинути його на <body>.
        if (rootRef.current && rootRef.current.contains(document.activeElement)) return;
        onScheduleClose();
      }}
      onBlur={(event) => {
        if (rootRef.current && rootRef.current.contains(event.relatedTarget)) return;
        // Клік мишею всередині панелі в Safari знімає фокус без нового
        // relatedTarget; поки курсор тут, панель лишається.
        if (hoverRef.current) return;
        onClose();
      }}
      onKeyDown={handleKeyDown}
    >
      <div className={pillClass(active)}>
        <Link to={to} className={wordClass(active)} aria-current={currentAttr(pathname, to)} onClick={onClose}>
          {label}
        </Link>
        <button
          ref={buttonRef}
          type="button"
          aria-label={`Меню розділу «${label}»`}
          aria-expanded={isOpen}
          aria-controls={`menu-${id}`}
          onClick={() => (isOpen ? onClose() : onOpen())}
          className={`-ml-2 pr-2.5 pl-0.5 py-2 transition-colors duration-200 ${
            onDark ? 'text-white/70 hover:text-white' : active ? 'text-teal-700' : 'text-slate-500 hover:text-teal-800'
          }`}
        >
          <Chevron className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <div className={`absolute left-0 top-full z-50 pt-2 ${width}`}>
            <motion.div
              ref={panelRef}
              id={`menu-${id}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 overflow-hidden"
            >
              {children}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Navbar = ({ solid, initialTone }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const navRef = useRef(null);
  const closeTimer = useRef(null);
  // У пререндереній сторінці шапка вже стоїть на місці до JavaScript, і
  // виїзд згори означав би, що вона спершу зникає за край екрана.
  const intro = useIntro();

  // До першого вимірювання тон підказує сама сторінка: під шапкою з банером
  // темне тло. Без підказки перший рендер розходився б із пререндереним
  // HTML, а при переході між сторінками шапка на кадр спалахувала б білим.
  const [navTone, setNavTone] = useState(initialTone || (solid ? 'light' : 'transparent'));

  useEffect(() => {
    let ticking = false;
    const measure = () => {
      if (window.scrollY <= 0 && !solid) return 'transparent';
      const height = navRef.current ? navRef.current.offsetHeight : 80;
      const probe = document.elementFromPoint(Math.floor(window.innerWidth / 2), height + 1);
      const dark = probe && typeof probe.closest === 'function' && probe.closest('[data-tone="dark"]');
      return dark ? 'dark' : 'light';
    };
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setNavTone(measure());
        ticking = false;
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [solid, pathname]);

  // Світлий текст потрібен і на прозорій шапці поверх відео, і на темній.
  const onDark = navTone !== 'light';

  // Після переходу на іншу сторінку всі списки згортаються.
  useEffect(() => {
    setOpenMenu(null);
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const openDropdown = (key) => {
    window.clearTimeout(closeTimer.current);
    setOpenMenu(key);
  };
  // Згортає лише свій список: фокус, що пішов із «Послуг», не має закривати
  // «Компанію», яку людина саме відкрила наведенням.
  const closeDropdown = (key) => {
    window.clearTimeout(closeTimer.current);
    setOpenMenu((current) => (current === key ? null : current));
  };
  // Невелика пауза перед згортанням: курсор, що йде з пункту в панель,
  // на мить опиняється поза обома — без паузи панель зникала б під рукою.
  const scheduleClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 140);
  };
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const goToContact = (source) => {
    trackEvent('cta_click', source);
    setIsMobileMenuOpen(false);
    if (pathname === '/kontakty') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate('/kontakty');
  };

  const handleLogoClick = (event) => {
    setIsMobileMenuOpen(false);
    // Уже на головній — залишаємось і просто піднімаємось нагору. Інакше
    // Router прибрав би якір із адреси й сторінка стрибнула б без анімації.
    if (pathname === '/') {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.mobile-menu-container')) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Escape згортає і мобільне меню, і випадний список, навіть якщо фокус
  // десь поза ними (список відкрито наведенням). Повернення фокуса — у
  // самому Dropdown і в ефекті мобільного меню нижче.
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setOpenMenu(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  /*
    Мобільне меню — модальний діалог. Поки воно відкрите, решта сторінки
    закрита затемненням, тож і фокус не має туди потрапляти: Tab ходить по
    колу всередині, а якщо фокус усе ж вийшов (диктор, клік у підкладку),
    повертаємо його назад. Після закриття фокус стає на бургер — інакше
    він лишався б на кнопці, якої вже немає, тобто на <body>.
  */
  const burgerRef = useRef(null);
  const dialogRef = useRef(null);
  const wasMobileOpen = useRef(false);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      if (!wasMobileOpen.current) return undefined;
      wasMobileOpen.current = false;
      const active = document.activeElement;
      const lost = !active || active === document.body || (dialogRef.current && dialogRef.current.contains(active));
      if (lost && burgerRef.current) burgerRef.current.focus({ preventScroll: true });
      return undefined;
    }

    wasMobileOpen.current = true;
    const [first] = focusablesIn(dialogRef.current);
    if (first) first.focus({ preventScroll: true });

    const keepFocusInside = (event) => {
      const dialog = dialogRef.current;
      if (!dialog || dialog.contains(event.target)) return;
      const [firstInside] = focusablesIn(dialog);
      if (firstInside) firstInside.focus({ preventScroll: true });
    };
    // Меню існує лише нижче lg. Якщо вікно розширили, поки воно відкрите,
    // діалог зникає з екрана, а пастка фокуса лишилася б — закриваємо.
    const wide = window.matchMedia('(min-width: 1024px)');
    const closeOnWide = () => {
      if (wide.matches) setIsMobileMenuOpen(false);
    };
    document.addEventListener('focusin', keepFocusInside);
    wide.addEventListener('change', closeOnWide);
    return () => {
      document.removeEventListener('focusin', keepFocusInside);
      wide.removeEventListener('change', closeOnWide);
    };
  }, [isMobileMenuOpen]);

  const handleDialogKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setIsMobileMenuOpen(false);
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusablesIn(dialogRef.current);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const isActive = (to) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(to + '/'));

  // Пункт меню — «пігулка»: підсвітка лягає на весь пункт разом зі
  // стрілкою, а не лише на слово. Раніше слово підсвічувалося, а стрілка
  // стояла поруч окремо, наче чужа.
  const pillClass = (active) =>
    `inline-flex items-center rounded-lg transition-colors duration-200 ${
      onDark
        ? active ? 'bg-white/10' : 'hover:bg-white/10'
        : active ? 'bg-teal-50' : 'hover:bg-slate-50'
    }`;
  const wordClass = (active) =>
    `px-3 py-2 text-[13px] xl:text-sm font-medium transition-colors duration-200 ${
      onDark
        ? active ? 'text-white' : 'text-white/90 hover:text-white'
        : active ? 'text-teal-700' : 'text-slate-700 hover:text-teal-800'
    }`;

  const dropdownProps = (id, to) => ({
    pathname,
    isOpen: openMenu === id,
    active: isActive(to) || openMenu === id,
    onDark,
    pillClass,
    wordClass,
    onOpen: () => openDropdown(id),
    onClose: () => closeDropdown(id),
    onScheduleClose: scheduleClose
  });

  return (
    <>
      <motion.nav
        ref={navRef}
        aria-label="Головне меню"
        className={`fixed top-0 w-full z-50 border-b transition-[background-color,box-shadow,border-color] duration-300 ${
          navTone === 'dark'
            ? 'bg-ink/90 backdrop-blur-lg border-white/10 shadow-lg'
            : navTone === 'light'
            ? 'bg-white backdrop-blur-lg border-transparent shadow-lg'
            : 'bg-transparent border-transparent'
        }`}
        initial={intro({ y: -100 })}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="section-container">
          {/*
            Три групи: логотип ліворуч, меню по центру, дії праворуч. Меню
            центрується абсолютно відносно рядка, а не «між» сусідами: інакше
            воно з'їжджало вбік залежно від ширини логотипа й кнопки.
          */}
          <div className="relative flex justify-between items-center h-16 lg:h-20">
            {/* Збільшення на наведенні — на CSS, а не через whileTap
                framer-motion: той ставить tabindex="0" на свою обгортку, і
                логотип забирав дві зупинки Tab, одну з них — на div без ролі. */}
            <div className="z-60 flex-shrink-0">
              <Link
                to="/"
                onClick={handleLogoClick}
                aria-label="Visia Medical Group — на головну"
                aria-current={currentAttr(pathname, '/')}
                className="flex items-center space-x-3 cursor-pointer transition-transform duration-200 hover:scale-105 active:scale-95"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-sky-600 rounded-lg flex items-center justify-center shadow-lg">
                  <span className="text-white font-black text-sm tracking-tight">VMG</span>
                </div>
                <div className="hidden sm:block">
                  <div className="flex flex-col">
                    <div className="flex items-baseline space-x-1">
                      <span className={`text-2xl font-black tracking-tight transition-colors ${
                        !onDark
                          ? 'bg-gradient-to-r from-teal-600 to-sky-500 bg-clip-text text-transparent'
                          : 'text-white drop-shadow-lg'
                      }`}>
                        VISIA
                      </span>
                      <span className={`text-sm font-light tracking-wide transition-colors ${
                        !onDark ? 'text-slate-500' : 'text-white/70'
                      }`}>
                        MEDICAL
                      </span>
                    </div>
                    <span className={`block text-xs font-medium uppercase tracking-[0.2em] -mt-0.5 transition-colors ${
                      !onDark ? 'text-slate-500' : 'text-white/60'
                    }`}>
                      GROUP
                    </span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1 xl:gap-2 absolute left-1/2 -translate-x-1/2">
              <Dropdown id="services" to="/poslugy" label="Послуги" width="w-[40rem]" {...dropdownProps('services', '/poslugy')}>
                <ul className="grid grid-cols-2 gap-1 p-3 list-none">
                  {SERVICE_LINKS.map((service) => (
                    <li key={service.to}>
                      <Link
                        to={service.to}
                        aria-current={currentAttr(pathname, service.to)}
                        className="group flex items-start gap-3 rounded-xl p-3 transition-colors duration-200 hover:bg-slate-50"
                      >
                        <span className="mt-0.5 w-10 h-10 flex-shrink-0 rounded-lg bg-teal-50 border border-teal-100 text-teal-700 flex items-center justify-center">
                          <ServiceGlyph index={service.index} className="w-6 h-6" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-slate-900 leading-snug group-hover:text-teal-800 transition-colors duration-200">
                            {service.title}
                          </span>
                          <span className="mt-0.5 text-xs text-slate-500 leading-snug line-clamp-2">
                            {service.text}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-100 bg-slate-50/70 px-6 py-3">
                  {SERVICES_MORE.map((link, i) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      aria-current={currentAttr(pathname, link.to)}
                      className={`inline-flex items-center gap-2 text-sm transition-colors duration-200 ${
                        i === 0 ? 'font-semibold text-teal-700 hover:text-teal-800' : 'text-slate-600 hover:text-teal-800'
                      }`}
                    >
                      {link.label}
                      {i === 0 && <Icon name="fa-arrow-right" className="text-xs" />}
                    </Link>
                  ))}
                </div>
              </Dropdown>

              <Dropdown id="company" to="/pro-kompaniyu" label="Компанія" width="w-72" {...dropdownProps('company', '/pro-kompaniyu')}>
                <ul className="p-2 list-none">
                  {COMPANY_LINKS.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        aria-current={currentAttr(pathname, link.to)}
                        className="group block rounded-xl px-4 py-3 transition-colors duration-200 hover:bg-slate-50"
                      >
                        <span className="block text-sm font-semibold text-slate-900 group-hover:text-teal-800 transition-colors duration-200">
                          {link.label}
                        </span>
                        <span className="block mt-0.5 text-xs text-slate-500">{link.text}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Dropdown>

              {TOP_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  aria-current={currentAttr(pathname, link.to)}
                  className={`${pillClass(isActive(link.to))} ${wordClass(isActive(link.to))}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-4 xl:gap-5 flex-shrink-0">
            {/* Месенджери — лише там, де є місце. */}
            <div className="hidden xl:flex items-center gap-3">
              {[TELEGRAM, VIBER].map((channel) => (
                <a
                  key={channel.label}
                  href={channel.href}
                  aria-label={channel.label}
                  title={channel.label}
                  onClick={() => trackEvent('contact_channel', `navbar-${channel.label.toLowerCase()}`)}
                  className={`transition-colors ${
                    !onDark ? 'text-slate-500 hover:text-teal-700' : 'text-white/70 hover:text-white'
                  }`}
                >
                  <Icon name={channel.icon} className="text-lg" />
                </a>
              ))}
            </div>

            <motion.button
              className="px-4 xl:px-6 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap bg-teal-700 text-white hover:bg-teal-800 shadow-sm transition-colors duration-300"
              onClick={() => goToContact('navbar')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Зв'язатися
            </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden mobile-menu-container flex items-center gap-1">
              {/* На телефоні дотик по номеру одразу починає дзвінок — найкоротший
                  шлях до звернення, і він не має бути захований у меню. */}
              <a
                href={PHONE.href}
                aria-label={`Зателефонувати: ${PHONE.label}`}
                onClick={() => trackEvent('contact_channel', 'navbar-mobile-phone')}
                className={`p-2 rounded-lg transition-colors ${
                  !onDark ? 'text-teal-700 hover:bg-teal-50' : 'text-white hover:bg-white/10'
                }`}
              >
                <Icon name="fa-phone" className="text-lg" />
              </a>
              {/* Назва стала: стан «відкрито/закрито» диктор бере з aria-expanded. */}
              <motion.button
                ref={burgerRef}
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg transition-colors relative z-60 ${
                  !onDark ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
                }`}
                whileTap={{ scale: 0.95 }}
                aria-label="Меню"
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <div className="w-6 h-6 flex flex-col justify-center space-y-1.5">
                  {[0, 1, 2].map((bar) => (
                    <motion.div
                      key={bar}
                      className={`w-full h-0.5 rounded-full origin-center ${!onDark ? 'bg-gray-700' : 'bg-white'}`}
                      animate={
                        bar === 1
                          ? { opacity: isMobileMenuOpen ? 0 : 1, scale: isMobileMenuOpen ? 0.8 : 1 }
                          : { rotate: isMobileMenuOpen ? (bar === 0 ? 45 : -45) : 0, y: isMobileMenuOpen ? (bar === 0 ? 6 : -6) : 0 }
                      }
                      transition={{ duration: 0.3 }}
                    />
                  ))}
                </div>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            <motion.div
              ref={dialogRef}
              id="mobile-menu"
              role="dialog"
              aria-modal="true"
              aria-label="Меню"
              onKeyDown={handleDialogKeyDown}
              className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 lg:hidden mobile-menu-container flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-sky-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-black text-xs">VMG</span>
                  </div>
                  <div>
                    <div className="text-lg font-black bg-gradient-to-r from-teal-600 to-sky-500 bg-clip-text text-transparent">
                      VISIA
                    </div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider -mt-1">
                      MEDICAL GROUP
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Закрити меню"
                >
                  <Icon name="fa-times" className="text-lg" />
                </button>
              </div>

              <div className="flex-1 flex flex-col min-h-0">
                {/* Дві групи замість плаского списку: ті самі адреси, що й у
                    випадних списках на десктопі. */}
                <nav className="flex-1 px-6 py-4 overflow-y-auto" aria-label="Меню сайту">
                  {MOBILE_GROUPS.map((group, gi) => (
                    <div key={group.title} className={gi > 0 ? 'mt-5 pt-5 border-t border-gray-100' : ''}>
                      <div className="px-4 mb-1 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                        {group.title}
                      </div>
                      <ul className="list-none">
                        {group.links.map((link, index) => (
                          <motion.li
                            key={link.to}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.05 + (gi * 8 + index) * 0.03, duration: 0.25 }}
                          >
                            <Link
                              to={link.to}
                              aria-current={currentAttr(pathname, link.to)}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`block py-2.5 px-4 rounded-lg transition-colors duration-200 ${
                                isActive(link.to)
                                  ? 'text-teal-700 bg-teal-50 font-medium'
                                  : 'text-gray-700 hover:text-teal-700 hover:bg-teal-50'
                              }`}
                            >
                              {link.label}
                            </Link>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </nav>

                <div className="flex-shrink-0 border-t border-gray-100">
                  <div className="px-6 py-4">
                    <motion.button
                      className="w-full px-6 py-4 bg-teal-700 text-white rounded-lg font-medium hover:bg-teal-800 transition-colors duration-200 flex items-center justify-center space-x-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.3 }}
                      onClick={() => goToContact('mobile-menu')}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span>Зв'язатися з нами</span>
                      <Icon name="fa-paper-plane" className="text-sm" />
                    </motion.button>
                  </div>

                  <div className="px-6 pb-6">
                    <motion.div
                      className="space-y-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.3 }}
                    >
                      <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">
                        Контакти
                      </div>
                      {[
                        { ...PHONE, icon: 'fas fa-phone', key: 'phone' },
                        { ...TELEGRAM, label: 'Telegram', key: 'telegram' },
                        { ...VIBER, label: 'Viber', key: 'viber' },
                        { ...EMAIL, icon: 'fas fa-envelope', key: 'email' }
                      ].map((channel) => (
                        <a
                          key={channel.key}
                          href={channel.href}
                          onClick={() => trackEvent('contact_channel', `mobile-menu-${channel.key}`)}
                          className="flex items-center space-x-3 text-sm text-gray-600 hover:text-teal-800 transition-colors"
                        >
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <Icon name={channel.icon} className="text-teal-500 text-xs" />
                          </div>
                          <span className="break-all">{channel.label}</span>
                        </a>
                      ))}
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
